import React, { useEffect } from 'react';
import { Button, Form, Input, Layout, Typography, message } from 'antd';
import { AppstoreOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../../store/useStore';
import { HomeLink } from '../../../components/HomeLink';

type AdminLoginForm = {
  loginName?: string;
  password?: string;
};

const { Title, Text } = Typography;

export const AdminLogin = observer(() => {
  const [form] = Form.useForm<AdminLoginForm>();
  const navigate = useNavigate();
  const location = useLocation();
  const { adminLoginStore } = useStore();

  useEffect(() => {
    if (adminLoginStore.data.hasLogin) {
      navigate('/devops/creator-outreach/users', { replace: true });
    }
  }, [adminLoginStore.data.hasLogin, navigate]);

  const onSubmit = async (values: AdminLoginForm) => {
    const loginName = values.loginName?.trim() ?? '';
    const password = values.password ?? '';
    try {
      const res = await adminLoginStore.login(loginName, password);
      if (res.code !== 200 || !adminLoginStore.data.hasLogin) {
        message.error(res.message || '登录失败');
        return;
      }
      message.success('登录成功');
      const fallback = '/devops/creator-outreach/users';
      const redirectTo = (location.state as { from?: { pathname?: string } })?.from?.pathname ?? fallback;
      navigate(redirectTo.startsWith('/devops/creator-outreach') ? redirectTo : fallback, { replace: true });
    } catch {
      message.error('登录失败，请稍后重试');
    }
  };

  return (
    <Layout className='admin-login-layout'>
      <HomeLink className='auth-home-link' />
      <div className='admin-login-card'>
        <div className='admin-login-brand'>
          <span className='admin-login-logo' aria-hidden>
            <AppstoreOutlined />
          </span>
          <div>
            <p className='admin-login-brand-title'>Wowbit 达人建联</p>
            <p className='admin-login-brand-sub'>内部运营管理后台</p>
          </div>
        </div>
        {/* <header className='admin-login-header'>
          <Title level={3} className='admin-login-title'>
            管理端登录
          </Title>
          <Text type='secondary'>仅限内部人员使用</Text>
        </header> */}
        <Form<AdminLoginForm>
          form={form}
          layout='vertical'
          requiredMark={false}
          onFinish={onSubmit}
        >
          <Form.Item
            name='loginName'
            label='账号'
            rules={[
              { required: true, whitespace: true, message: '请输入账号' },
              { max: 128, message: '登录名最长 128 个字符' }
            ]}
          >
            <Input
              prefix={<UserOutlined className='site-form-item-icon' />}
              placeholder='请输入运营账号'
              size='large'
              maxLength={128}
            />
          </Form.Item>
          <Form.Item
            name='password'
            label='密码'
            rules={[
              { required: true, message: '请输入密码' },
              {
                validator: (_, value: string) => {
                  const password = value ?? '';
                  if (!password) return Promise.resolve();
                  const bytes = new TextEncoder().encode(password).length;
                  if (password.length <= 72 && bytes <= 72) return Promise.resolve();
                  return Promise.reject(new Error('密码不超过 72 个字符，且 UTF-8 不超过 72 字节'));
                }
              }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className='site-form-item-icon' />}
              placeholder='请输入密码'
              size='large'
              maxLength={72}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
            <Button type='primary' htmlType='submit' block size='large' className='admin-login-submit'>
              登录
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Layout>
  );
});
